import { API_URL } from '../config/api';
import { getSessionId, getVisitorId } from '../utils/trackingIdentity';
import { clearToken, getToken } from '../utils/auth';
import { showError } from '../utils/notify';

const OFFLINE_MESSAGE = 'Please check your internet connection.';
const OFFLINE_TOAST_DEDUP_MS = 5000;
const GENERIC_REQUEST_ERROR = 'We could not complete your request right now. Please try again.';
const GENERIC_SERVER_ERROR = 'Something went wrong. Please try again.';
const NETWORK_ERROR_MESSAGE = 'Unable to reach the server right now. Please try again.';

let lastOfflineToastAt = 0;

const ERROR_CODE_MESSAGES = {
  ai_processing_failed: 'We could not process that request right now. Please try again.',
  ai_request_failed: 'AI is not available right now. Please try again.',
  ai_unavailable: 'AI is busy right now. Please try again shortly.',
  image_required: 'Please upload an image to continue.',
  internal_server_error: GENERIC_SERVER_ERROR,
  invalid_request: 'Please check your details and try again.',
  not_authorized: 'You do not have permission to do that.',
  not_found: 'We could not find what you requested.',
  otp_delivery_failed: 'We could not send the code right now. Please try again.',
  payment_failed: 'We could not complete the payment right now. Please try again.',
  provider_error: 'This service is busy right now. Please try again.',
  token_balance_not_found: 'AI usage is not available for this account yet.',
  unauthorized: 'Please log in to continue.',
  verification_failed: 'We could not complete verification right now. Please try again.'
};

function buildUrl(path, query) {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (!API_URL) {
    throw new Error('Missing API base URL (API_URL). Check src/config/api.js');
  }
  const baseUrl = /^https?:\/\//i.test(API_URL)
    ? API_URL
    : typeof window !== 'undefined'
      ? new URL(API_URL, window.location.origin).toString()
      : API_URL;
  const url = new URL(`${baseUrl}${normalizedPath}`);

  if (query && typeof query === 'object') {
    Object.entries(query).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;

      if (Array.isArray(value)) {
        value.forEach((item) => url.searchParams.append(`${key}[]`, String(item)));
        return;
      }

      url.searchParams.set(key, String(value));
    });
  }

  return url.toString();
}

function isBrowserOffline() {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

function showOfflineError() {
  const now = Date.now();
  if (now - lastOfflineToastAt < OFFLINE_TOAST_DEDUP_MS) {
    return;
  }

  lastOfflineToastAt = now;
  showError(OFFLINE_MESSAGE);
}

export function resetApiClientTestState() {
  lastOfflineToastAt = 0;
}

function statusMessage(status) {
  switch (status) {
    case 400:
    case 422:
      return 'Please check your details and try again.';
    case 401:
      return 'Please log in to continue.';
    case 403:
      return 'You do not have permission to do that.';
    case 404:
      return 'We could not find what you requested.';
    case 408:
    case 429:
      return 'Too many requests right now. Please try again shortly.';
    default:
      return status >= 500 ? GENERIC_SERVER_ERROR : GENERIC_REQUEST_ERROR;
  }
}

function sanitizeMessage(value) {
  if (!value) return null;

  const message = String(value).trim();
  if (!message) return null;

  if (/gemini|rollbar|scout|paystack|pidasys|dojah|provider|postgres|mysql|redis|jwt/i.test(message)) {
    return null;
  }

  if (/^\d{3}\s/.test(message)) {
    return null;
  }

  if (/not found|unauthorized|forbidden|internal server error/i.test(message)) {
    return null;
  }

  if (/exception|stack trace|undefined method|timeout::error|net::/i.test(message)) {
    return null;
  }

  return message;
}

function errorMessageForResponse(response, parsed) {
  const code = parsed?.error ? String(parsed.error) : null;
  const safeParsedMessage = sanitizeMessage(parsed?.message);
  const safeParsedError = sanitizeMessage(parsed?.error);

  if (code === 'validation_failed') {
    return safeParsedMessage || 'Please check the highlighted details and try again.';
  }

  return (
    safeParsedMessage ||
    (code && ERROR_CODE_MESSAGES[code]) ||
    safeParsedError ||
    statusMessage(response.status)
  );
}

async function request(path, options = {}) {
  const { query, headers, parse = 'json', ...rest } = options;

  const token = getToken();
  const finalHeaders = {
    Accept: 'application/json',
    ...(headers || {}),
  };

  const visitorId = getVisitorId();
  const sessionId = getSessionId();

  if (visitorId && !finalHeaders['X-Visitor-Id']) {
    finalHeaders['X-Visitor-Id'] = visitorId;
  }

  if (sessionId && !finalHeaders['X-Session-Id']) {
    finalHeaders['X-Session-Id'] = sessionId;
  }

  if (!finalHeaders.Authorization && token) {
    finalHeaders.Authorization = `Bearer ${token}`;
  }

  if (isBrowserOffline()) {
    showOfflineError();
    const err = new Error(OFFLINE_MESSAGE);
    err.status = 0;
    err.code = 'offline';
    throw err;
  }

  let response;
  try {
    response = await fetch(buildUrl(path, query), {
      headers: finalHeaders,
      ...rest,
    });
  } catch (networkErr) {
    // If the request was aborted, rethrow the original AbortError so callers can detect it.
    if (networkErr && networkErr.name === 'AbortError') {
      throw networkErr;
    }

    if (isBrowserOffline()) {
      showOfflineError();
      const err = new Error(OFFLINE_MESSAGE);
      err.status = 0;
      err.code = 'offline';
      throw err;
    }

    if (!options.suppressError) showError(NETWORK_ERROR_MESSAGE);
    const err = new Error(NETWORK_ERROR_MESSAGE);
    err.status = 0;
    err.cause = networkErr;
    throw err;
  }

  if (!response.ok) {
    let parsed = null;
    let bodyMsg = statusMessage(response.status);
    try {
      parsed = await response.json().catch(() => null);
      bodyMsg = errorMessageForResponse(response, parsed);
    } catch {
      // ignore parse errors
    }

    if (!options.suppressError) showError(bodyMsg);

    // Handle authentication errors globally: clear stored token and redirect to login.
    // Only clear the token and fire the session-expired event for non-suppressed calls.
    // suppressError calls (e.g. fetchShopForUser) handle their own errors and must not
    // wipe the token — doing so would invalidate subsequent non-suppressed API calls.
    if (response.status === 401 || response.status === 403) {
      if (!options.suppressError) {
        const currentToken = getToken();
        const requestOwnsCurrentSession = token && currentToken === token;

        if (requestOwnsCurrentSession) {
          try {
            clearToken();
          } catch (err) {
            void err;
          }
          try {
            if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
              window.dispatchEvent(new CustomEvent('yubu-session-expired', { detail: { token } }));
            }
          } catch (err) {
            void err;
          }
        }
      }
    }

    const error = new Error(bodyMsg);
    error.status = response.status;
    error.response = response;
    if (parsed?.error) error.code = parsed.error;
    if (Array.isArray(parsed?.details)) error.details = parsed.details;
    throw error;
  }

  if (parse === 'text') return response.text();
  if (parse === 'blob') return response.blob();
  if (parse === 'response') return response;

  // Handle No Content responses (unchanged DELETE semantics)
  if (response.status === 204) return null;

  // Default: parse JSON and normalize new API envelope
  const contentType = (response.headers.get('content-type') || '').toLowerCase();
  const rawBody = await response.text().catch(() => '');

  if (!rawBody) return null;

  if (!contentType.includes('application/json')) {
    const message = GENERIC_SERVER_ERROR;

    if (!options.suppressError) showError(message);

    const err = new Error(message);
    err.status = response.status;
    err.response = response;
    throw err;
  }

  let parsed = null;
  try {
    parsed = JSON.parse(rawBody);
  } catch {
    const message = GENERIC_SERVER_ERROR;
    if (!options.suppressError) showError(message);
    const err = new Error(message);
    err.status = response.status;
    err.response = response;
    throw err;
  }

  // If the server uses the new envelope { success: true, data: ... }
  if (parsed && typeof parsed === 'object') {
    if (parsed.success === true && Object.prototype.hasOwnProperty.call(parsed, 'data')) {
      return parsed.data;
    }

    // Validation errors: { error: 'validation_failed', details: [...] }
    if (parsed.error === 'validation_failed') {
      const err = new Error(
        sanitizeMessage(parsed.message) || 'Please check the highlighted details and try again.'
      );
      err.code = 'validation_failed';
      err.details = Array.isArray(parsed.details) ? parsed.details : [];
      err.fieldErrors =
        parsed.field_errors && typeof parsed.field_errors === 'object' ? parsed.field_errors : {};
      err.status = response.status;
      throw err;
    }

    // Other error shapes: { error: 'code', message?: '...' }
    if (parsed.error) {
      const msg = errorMessageForResponse(
        { status: response.status },
        parsed
      );
      const err = new Error(msg);
      err.code = parsed.error;
      err.status = response.status;
      throw err;
    }
  }

  // Fallback: return whatever parsed JSON we got (legacy shape)
  return parsed;
}

export const apiClient = {
  buildUrl,
  request,
  authHeader() {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  },
  get(path, options = {}) {
    return request(path, { ...options, method: 'GET' });
  },
  post(path, body, options = {}) {
    return request(path, {
      ...options,
      method: 'POST',
      body: body instanceof FormData ? body : JSON.stringify(body),
      headers:
        body instanceof FormData
          ? options.headers
          : {
              'Content-Type': 'application/json',
              ...options.headers,
            },
    });
  },
  put(path, body, options = {}) {
    return request(path, {
      ...options,
      method: 'PUT',
      body: body instanceof FormData ? body : JSON.stringify(body),
      headers:
        body instanceof FormData
          ? options.headers
          : {
              'Content-Type': 'application/json',
              ...options.headers,
            },
    });
  },
  patch(path, body, options = {}) {
    return request(path, {
      ...options,
      method: 'PATCH',
      body: body instanceof FormData ? body : JSON.stringify(body),
      headers:
        body instanceof FormData
          ? options.headers
          : {
              'Content-Type': 'application/json',
              ...options.headers,
            },
    });
  },
  delete(path, options = {}) {
    return request(path, { ...options, method: 'DELETE' });
  },
};

export default apiClient;
